import { memo, useState, useRef, useCallback, useEffect } from 'react'
import { BottomNavBar } from '../components/BottomNavBar'
import TopNavBar from '../components/TopNavBar'
import { ComandosDaRodada } from '../turno/ComandosDaRodada'
import { FilaDoTurno } from '../turno/FilaDoTurno'
import { ChatInputContainer } from '../GameControls/ChatInputContainer'
import { useChatContext, useTurnContext } from '../contexts'
import type { Character } from '@/types'
import type { Member as MemberType } from '@/types/member'

interface MobileLayoutProps {
  activeTab: 'members' | 'chat' | 'private' | 'items' | 'abilities' | 'media' | 'notes'
  totalUnread?: number
  groupUnread?: number
  onTabChange: (tab: 'members' | 'chat' | 'private' | 'items' | 'abilities' | 'media' | 'notes') => void

  // Mesa e participantes. O que era exclusivo do SidePanel saiu com ele em
  // 2026-07-31 (userFullName, userRole, onCharacterChatClick, unreadCounts,
  // isUserOnline, e o bloco do sorteio) — agora chega no SidePanel pelo
  // `tabContent` do GameSession, sem passar por aqui.
  members: MemberType[]
  characters: Character[]
  tableName?: string
  /** Missao ativa no formato "Missao 3: O laboratorio" — sobe para a TopNavBar */
  missionTitle?: string
  currentUserId?: string

  // Conteudo das abas (tabContent memoizado no GameSession) — como no desktop
  children: React.ReactNode

  // ChatInput props. A maioria saiu — o ChatInputContainer lê do ChatContext
  // (onRollDice/onLiveSync/onPoll/onLab/isConnected/loadingHistory/
  // loadingPrivateHistory/isMaster/respondingAsNpc/onToggleRespondAsNpc) e do
  // TurnContext. isLoading e selectedPrivateUser ainda são passados.
  isLoading: boolean
  selectedPrivateUser?: MemberType | null
  tableId?: string
  onNavigate?: (path: string) => void
  onOpenSettings?: () => void
  /** Aluno sem ficha não fala (criação mediada, fase 3a). */
  chatDisabled?: boolean
}

// Custom comparison for React.memo - focus on frequently changing props
function arePropsEqual(prev: MobileLayoutProps, next: MobileLayoutProps): boolean {
  // UI state
  if (prev.activeTab !== next.activeTab) return false
  if (prev.totalUnread !== next.totalUnread) return false
  if (prev.groupUnread !== next.groupUnread) return false

  // Loading state
  if (prev.isLoading !== next.isLoading) return false

  // User info
  if (prev.currentUserId !== next.currentUserId) return false

  // Selected entities
  if (prev.selectedPrivateUser?.userId !== next.selectedPrivateUser?.userId) return false

  // Arrays by reference
  if (prev.members !== next.members) return false
  if (prev.characters !== next.characters) return false

  // Children (tabContent memoizado no GameSession — so muda quando o
  // conteudo real do chat/aba muda)
  if (prev.children !== next.children) return false

  // Static props
  if (prev.tableId !== next.tableId) return false
  if (prev.tableName !== next.tableName) return false
  if (prev.missionTitle !== next.missionTitle) return false

  if (prev.chatDisabled !== next.chatDisabled) return false

  return true
}

export const MobileLayout = memo(function MobileLayout({
  activeTab,
  totalUnread = 0,
  groupUnread = 0,
  onTabChange,
  members,
  characters,
  tableName,
  missionTitle,
  currentUserId,
  children,
  isLoading,
  selectedPrivateUser,
  tableId,
  onNavigate,
  onOpenSettings,
  chatDisabled
}: MobileLayoutProps) {
  const { chatMode } = useChatContext()
  const { turnState } = useTurnContext()

  // Enquanto o usuario digita (textarea focado), a BottomNavBar some para
  // devolver ~68px ao chat — exatamente quando o teclado ja comeu a tela.
  // O blur espera 150ms antes de trazer a barra de volta: sem isso, tocar
  // no botao de enviar (que tira o foco por um instante) fazia a barra
  // piscar a cada mensagem.
  const [inputFocused, setInputFocused] = useState(false)
  const blurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const handleInputFocusChange = useCallback((focused: boolean) => {
    if (blurTimerRef.current) {
      clearTimeout(blurTimerRef.current)
      blurTimerRef.current = null
    }
    if (focused) {
      setInputFocused(true)
    } else {
      blurTimerRef.current = setTimeout(() => setInputFocused(false), 150)
    }
  }, [])
  useEffect(() => () => {
    if (blurTimerRef.current) clearTimeout(blurTimerRef.current)
  }, [])

  return (
    // Fase B mobile: coluna flex real, como o desktop. TopNavBar, conteudo,
    // input e BottomNavBar empilhados NO FLUXO — nada de position fixed nem
    // padding compensatorio no conteudo. O pai (GameSession) fornece a coluna
    // (flex-col h-full); este fragmento sao os filhos dela, em ordem.
    <>
      {/* O painel de membros saiu daqui em 2026-07-31
          (docs/PLANO_MEMBROS_E_FICHA.md). "Membros" virou uma aba como as
          outras: o SidePanel agora é renderizado pelo `tabContent` do
          GameSession, no fluxo, e não mais num Drawer por cima do jogo.
          Com isso some também o Drawer (vaul) dentro do qual a ficha — que
          também é vaul — abria. */}

      {/* TopNavBar le turno/conexao dos contexts; a legenda (missao ou nome da
          mesa) vem por prop e so aparece quando nao ha rodada correndo */}
      <TopNavBar tableName={tableName} missionTitle={missionTitle} />

      {/* A fila da rodada, visivel para todo mundo (fase 5): o estudante
          precisa saber quantas vezes faltam ate a dele. Some quando nao ha
          rodada. */}
      <FilaDoTurno currentUserId={currentUserId} />

      {/* Conteudo das abas - ocupa o espaco restante da coluna */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {children}
      </div>

      {/* Os comandos da rodada, na altura do polegar (2026-07-27). Ficam
          entre o conteudo e a caixa de mensagem, no fluxo da coluna. Somem
          enquanto se digita, junto com a BottomNavBar: com o teclado aberto
          nao se conduz a rodada, e o espaco vai para as mensagens. */}
      {!inputFocused && <ComandosDaRodada />}

      {/* Input de chat - no fluxo, so nas abas de chat. A altura variavel
          (textarea crescendo, upload) empurra o conteudo acima por
          construcao — sem esconder mensagens. */}
      {(activeTab === 'chat' || chatMode === 'private') && (
        <div className="flex-shrink-0">
          <ChatInputContainer
            disabled={isLoading || chatDisabled}
            isLoading={isLoading}
            selectedPrivateUser={selectedPrivateUser}
            currentUserId={currentUserId}
            tableId={tableId}
            isActive={turnState?.isActive}
            characters={characters}
            members={members}
            onFocusChange={handleInputFocusChange}
          />
        </div>
      )}

      {/* Barra de navegacao inferior - ultimo item da coluna. Some enquanto
          o usuario digita (nao se navega de aba com o teclado aberto) e o
          espaco vai para as mensagens. */}
      {!inputFocused && (
        <BottomNavBar
          activeTab={activeTab === 'chat' && chatMode === 'private' ? 'private' : activeTab}
          totalUnread={totalUnread}
          groupUnread={groupUnread}
          onTabChange={onTabChange}
          onNavigate={onNavigate}
          onOpenSettings={onOpenSettings}
        />
      )}
    </>
  )
}, arePropsEqual)